const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const GEOJS_GEO_URL = 'https://get.geojs.io/v1/ip/geo.json';
const US_COUNTRY_CODE = 'US';
const TARGET_REGION_CODE = 'CA';
const TARGET_REGION_NAMES = ['CALIFORNIA', 'CA'];
const TARGET_CITY_NAMES = ['LOS ANGELES', 'LOSANGELES'];
const REGION_CACHE_KEY = 'californiaPrivacyRegionCacheV5';
const REGION_CACHE_TTL = 24 * 60 * 60 * 1000;
const PRIVACY_API_TIMEOUT_MS = 3000;
const PRIVACY_API_POLL_MS = 50;
const GEO_FETCH_TIMEOUT_MS = 4000;
const BODY_MODAL_OPEN_CLASS = 'california-model-open';
const HIDE_SHOPIFY_BANNER_CLASS = 'california-privacy--hide-shopify-banner';
const SHOPIFY_PRIVACY_BANNER_SELECTORS = [
  '#shopify-pc__banner',
  '#shopify-pc_banner',
  '.shopify-pc__banner_dialog',
  '.shopify-pc_banner_dialog',
  'section[id^="shopify-pc"]',
];
const SHOPIFY_BANNER_OBSERVER_MS = 15000;

class CaliforniaPrivacyPopup extends HTMLElement {
  constructor() {
    super();

    this.banner = null;
    this.modalContainer = null;
    this.modal = null;
    this.modalBody = null;
    this.overlay = null;
    this.bannerOverlay = null;
    this.pageOverlay = null;
    this.checkboxes = [];
    this.saveButton = null;
    this.lastFocusedElement = null;
    this.initialized = false;
    this.initStarted = false;
    this.consentSaved = false;

    this.onKeyDown = this.onKeyDown.bind(this);
    this.onComponentClick = this.onComponentClick.bind(this);
  }

  connectedCallback() {
    if (this.initialized || this.initStarted) return;

    this.initStarted = true;
    this.cacheElements();
    this.init();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('click', this.onComponentClick);
    document.body.classList.remove(BODY_MODAL_OPEN_CLASS);
    this.stopShopifyBannerObserver();
  }

  cacheElements() {
    this.banner = this.querySelector('.js-california-privacy-banner');
    this.modalContainer = this.querySelector('.js-california-privacy-modal-container');
    this.modal = this.querySelector('.js-california-privacy-modal');
    this.modalBody = this.querySelector('.js-california-privacy-modal-body');
    this.overlay = this.querySelector('.js-california-privacy-overlay');
    this.bannerOverlay = this.querySelector('.js-california-privacy-banner-overlay');
    this.pageOverlay = this.querySelector('.js-california-privacy-page-overlay');
    this.checkboxes = this.querySelectorAll('.js-california-privacy-checkbox');
    this.saveButton = this.querySelector('.js-california-privacy-save-choices');
  }

  isDesignMode() {
    return (
      this.dataset.designMode === 'true' ||
      window.Shopify?.designMode === true ||
      document.documentElement.classList.contains('shopify-design-mode')
    );
  }

  // Unpublished/preview theme link (not the theme editor - that's isDesignMode).
  // Real eligibility still runs; this just prevents the Shopify default banner
  // from flashing on screen while that check is in flight.
  isThemePreview() {
    return this.dataset.themePreview === 'true';
  }

  bindDesignModeEvents() {
    if (!this.isDesignMode() || this.designModeEventsBound) return;

    this.designModeEventsBound = true;
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;

      this.consentSaved = false;
      this.initialized = false;
      this.initStarted = false;
      this.hidden = true;
      this.cacheElements();
      this.init();
    });
  }

  async init() {
    if (this.isDesignMode()) {
      this.consentSaved = false;
      this.bindDesignModeEvents();
    }

    if (this.shouldResetForTesting()) {
      this.resetTestingState();
    }

    const popups = document.querySelectorAll('california-privacy-popup');
    if (popups.length > 1 && popups[0] !== this) {
      this.hidden = true;
      return;
    }

    // Push known CA state immediately (cache / URL / calpak) before Privacy API waits.
    this.syncKnownRegionStateEarly();

    if (this.shouldHideShopifyBannerEarly()) {
      this.hideShopifyPrivacyBanner();
    }

    const eligibilityPromise = this.isEligibleVisitor();

    await Promise.all([this.waitForCustomerPrivacy(), this.ensureStylesLoaded()]);

    const isEligibleVisitor = this.isDesignMode() ? true : await eligibilityPromise;

    if (!isEligibleVisitor) {
      this.restoreShopifyPrivacyBanner();
      this.hidden = true;
      return;
    }

    this.hideShopifyPrivacyBanner();

    if (!this.shouldShowPopup()) {
      this.hidden = true;
      return;
    }

    this.initialized = true;
    this.consentSaved = false;
    this.bindEvents();
    this.showBanner();
  }

  ensureStylesLoaded() {
    return new Promise((resolve) => {
      const stylesheet = document.querySelector('link[href*="section-california-privacy"]');
      if (!stylesheet) {
        resolve();
        return;
      }

      if (stylesheet.sheet) {
        resolve();
        return;
      }

      const done = () => resolve();
      stylesheet.addEventListener('load', done, { once: true });
      stylesheet.addEventListener('error', done, { once: true });
      window.setTimeout(done, 1500);
    });
  }

  fetchWithTimeout(url, timeoutMs) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    return fetch(url, { signal: controller.signal }).finally(() => {
      window.clearTimeout(timeoutId);
    });
  }

  shouldResetForTesting() {
    const params = new URLSearchParams(window.location.search);
    return params.has('ca_privacy_reset');
  }

  shouldHideShopifyBannerEarly() {
    if (this.isDesignMode()) return true;
    if (this.isThemePreview()) return true;

    const testOverride = this.getRegionTestOverride();
    if (testOverride === true) return true;
    if (testOverride === false) return false;

    const cachedResult = this.getCachedRegionResult();
    if (cachedResult === true) return true;

    const calpakResult = this.getCalpakRegionResult();
    return calpakResult === true;
  }

  hideShopifyPrivacyBanner() {
    document.body.classList.add(HIDE_SHOPIFY_BANNER_CLASS);
    this.applyShopifyPrivacyBannerVisibility(true);
    this.startShopifyBannerObserver();
  }

  restoreShopifyPrivacyBanner() {
    document.body.classList.remove(HIDE_SHOPIFY_BANNER_CLASS);
    this.applyShopifyPrivacyBannerVisibility(false);
    this.stopShopifyBannerObserver();
  }

  applyShopifyPrivacyBannerVisibility(shouldHide) {
    SHOPIFY_PRIVACY_BANNER_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        if (shouldHide) {
          element.setAttribute('hidden', '');
          element.setAttribute('aria-hidden', 'true');
        } else {
          element.removeAttribute('hidden');
          element.removeAttribute('aria-hidden');
        }
      });
    });
  }

  startShopifyBannerObserver() {
    if (this.shopifyBannerObserver) return;

    const hideIfPresent = () => {
      if (!document.body.classList.contains(HIDE_SHOPIFY_BANNER_CLASS)) return;
      this.applyShopifyPrivacyBannerVisibility(true);
    };

    this.shopifyBannerObserver = new MutationObserver(hideIfPresent);
    this.shopifyBannerObserver.observe(document.body, { childList: true, subtree: true });

    this.shopifyBannerObserverTimeout = window.setTimeout(() => {
      this.stopShopifyBannerObserver();
    }, SHOPIFY_BANNER_OBSERVER_MS);
  }

  stopShopifyBannerObserver() {
    if (this.shopifyBannerObserverTimeout) {
      window.clearTimeout(this.shopifyBannerObserverTimeout);
      this.shopifyBannerObserverTimeout = null;
    }

    if (this.shopifyBannerObserver) {
      this.shopifyBannerObserver.disconnect();
      this.shopifyBannerObserver = null;
    }
  }

  resetTestingState() {
    try {
      window.localStorage.removeItem(REGION_CACHE_KEY);
    } catch (error) {
      // Ignore storage failures.
    }

    window.calpak = window.calpak || {};
    window.calpak.isCaliforniaActive = false;
    window.calpak.__californiaActiveResolved = false;
    this.consentSaved = false;
    this.hidden = true;
    document.body.classList.remove(BODY_MODAL_OPEN_CLASS);
    this.restoreShopifyPrivacyBanner();
  }

  reveal() {
    this.hidden = false;
    this.removeAttribute('hidden');
  }

  waitForCustomerPrivacy() {
    return new Promise((resolve) => {
      if (window.Shopify?.customerPrivacy) {
        resolve();
        return;
      }

      const interval = setInterval(() => {
        if (window.Shopify?.customerPrivacy) {
          clearInterval(interval);
          resolve();
        }
      }, PRIVACY_API_POLL_MS);

      setTimeout(() => {
        clearInterval(interval);
        resolve();
      }, PRIVACY_API_TIMEOUT_MS);
    });
  }

  shouldShowPopup() {
    if (this.isDesignMode()) {
      return true;
    }

    const privacy = window.Shopify?.customerPrivacy;

    if (privacy && this.hasConsentDecision()) {
      return false;
    }

    if (privacy?.shouldShowBanner?.()) {
      return true;
    }

    return true;
  }

  getRegionTestOverride() {
    const params = new URLSearchParams(window.location.search);
    const testValue = params.get('ca_privacy_test') || params.get('ca_privacy_region');
    if (!testValue) return null;

    const normalized = testValue.trim().toUpperCase().replace(/\s+/g, '');
    if (normalized === 'CA' || normalized === 'CALIFORNIA') return true;
    if (normalized === 'LA' || normalized === 'LOSANGELES') return true;
    if (normalized === 'NONCA' || normalized === 'NON-CA') return false;

    return null;
  }

  getCachedRegionResult() {
    try {
      const cached = window.localStorage.getItem(REGION_CACHE_KEY);
      if (!cached) return null;

      const { timestamp, isEligible } = JSON.parse(cached);
      if (Date.now() - timestamp > REGION_CACHE_TTL) {
        window.localStorage.removeItem(REGION_CACHE_KEY);
        return null;
      }

      return Boolean(isEligible);
    } catch (error) {
      return null;
    }
  }

  setCachedRegionResult(isEligible) {
    try {
      window.localStorage.setItem(
        REGION_CACHE_KEY,
        JSON.stringify({
          timestamp: Date.now(),
          isEligible,
        })
      );
    } catch (error) {
      // Ignore storage failures.
    }
  }

  normalizeLocationName(value) {
    return (value || '').trim().toUpperCase().replace(/\s+/g, ' ');
  }

  normalizeCompactName(value) {
    return this.normalizeLocationName(value).replace(/\s+/g, '');
  }

  matchesTargetCity(cityName) {
    const normalizedCity = this.normalizeCompactName(cityName);
    if (!normalizedCity) return false;

    return TARGET_CITY_NAMES.some((name) => normalizedCity === name.replace(/\s+/g, ''));
  }

  matchesTargetRegion(regionCode, regionName) {
    const code = (regionCode || '').trim().toUpperCase();
    if (code === TARGET_REGION_CODE) {
      return true;
    }

    const normalizedName = this.normalizeCompactName(regionName);
    return TARGET_REGION_NAMES.some((name) => normalizedName === name.replace(/\s+/g, ''));
  }

  isEligibleLocation(regionCode, regionName, cityName) {
    return this.matchesTargetRegion(regionCode, regionName) || this.matchesTargetCity(cityName);
  }

  pushIsCaliforniaActive(isCaliforniaActive, options = {}) {
    const nextValue = Boolean(isCaliforniaActive);
    window.calpak = window.calpak || {};

    const previousValue = window.calpak.isCaliforniaActive;
    const forcePush = Boolean(options.forcePush);
    const alreadySet = previousValue === nextValue && window.calpak.__californiaActiveResolved === true;

    window.calpak.isCaliforniaActive = nextValue;
    window.calpak.__californiaActiveResolved = true;

    // Avoid duplicate dataLayer noise when early sync already set the same value.
    if (alreadySet && !forcePush) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ isCaliforniaActive: nextValue });
  }

  /**
   * Immediately publish any already-known region result so GTM does not keep
   * the hardcoded false from page boot while geo/Privacy API are still loading.
   */
  syncKnownRegionStateEarly() {
    if (this.isDesignMode()) {
      this.pushIsCaliforniaActive(true);
      return true;
    }

    const testOverride = this.getRegionTestOverride();
    if (testOverride !== null) {
      this.pushIsCaliforniaActive(testOverride);
      this.setCachedRegionResult(testOverride);
      return testOverride;
    }

    const cachedResult = this.getCachedRegionResult();
    if (cachedResult !== null) {
      this.pushIsCaliforniaActive(cachedResult);
      return cachedResult;
    }

    const calpakResult = this.getCalpakRegionResult();
    if (calpakResult !== null) {
      this.syncIsCaliforniaActive(
        window.calpak?.userRegionCode,
        window.calpak?.userRegionName,
        window.calpak?.userCountryCode,
        window.calpak?.userLocationData?.city || window.calpak?.userCityName
      );
      this.setCachedRegionResult(calpakResult);
      return calpakResult;
    }

    return null;
  }

  syncIsCaliforniaActive(regionCode, regionName, countryCode, cityName) {
    if (countryCode && countryCode.toUpperCase() !== US_COUNTRY_CODE) {
      this.pushIsCaliforniaActive(false);
      return;
    }

    this.pushIsCaliforniaActive(this.isEligibleLocation(regionCode, regionName, cityName));
  }

  getCalpakRegionResult() {
    const regionCode = window.calpak?.userRegionCode;
    const regionName = window.calpak?.userRegionName;
    const cityName = window.calpak?.userLocationData?.city || window.calpak?.userCityName;
    const countryCode = window.calpak?.userCountryCode;

    if (countryCode && countryCode.toUpperCase() !== US_COUNTRY_CODE) {
      return false;
    }

    if (regionCode || regionName || cityName) {
      return this.isEligibleLocation(regionCode, regionName, cityName);
    }

    return null;
  }

  async isEligibleVisitor() {
    if (this.isDesignMode()) {
      this.pushIsCaliforniaActive(true);
      return true;
    }

    const testOverride = this.getRegionTestOverride();
    if (testOverride !== null) {
      this.pushIsCaliforniaActive(testOverride);
      // Persist so the next page (e.g. PDP) keeps isCaliforniaActive without the URL param.
      this.setCachedRegionResult(testOverride);
      return testOverride;
    }

    // Cache is only used for early optimistic paint (syncKnownRegionStateEarly) and as a
    // fallback when the real check below fails. Eligibility itself is always re-verified
    // against the visitor's current location so travel in/out of CA is picked up per page load.
    const calpakResult = this.getCalpakRegionResult();
    if (calpakResult !== null) {
      this.syncIsCaliforniaActive(
        window.calpak?.userRegionCode,
        window.calpak?.userRegionName,
        window.calpak?.userCountryCode,
        window.calpak?.userLocationData?.city || window.calpak?.userCityName
      );
      this.setCachedRegionResult(calpakResult);
      return calpakResult;
    }

    try {
      const geoResponse = await this.fetchWithTimeout(GEOJS_GEO_URL, GEO_FETCH_TIMEOUT_MS);
      if (!geoResponse.ok) {
        return this.fallBackToCacheOrDefault();
      }

      const geo = await geoResponse.json();
      const countryCode = (geo?.country_code || geo?.countryCode || '').toUpperCase();
      const regionCode = geo?.region_code || geo?.regionCode || '';
      const regionName = geo?.region || geo?.region_name || geo?.state || '';
      const cityName = geo?.city || geo?.city_name || '';

      if (countryCode && countryCode !== US_COUNTRY_CODE) {
        this.syncIsCaliforniaActive(regionCode, regionName, countryCode, cityName);
        this.setCachedRegionResult(false);
        return false;
      }

      this.syncIsCaliforniaActive(regionCode, regionName, countryCode, cityName);

      const isEligible = this.isEligibleLocation(regionCode, regionName, cityName);
      this.setCachedRegionResult(isEligible);
      return isEligible;
    } catch (error) {
      return this.fallBackToCacheOrDefault();
    }
  }

  // Real-time detection failed (network/timeout) - prefer a stale-but-real cached
  // result over blindly defaulting to non-California.
  fallBackToCacheOrDefault() {
    const cachedResult = this.getCachedRegionResult();
    if (cachedResult !== null) {
      this.pushIsCaliforniaActive(cachedResult);
      return cachedResult;
    }

    this.pushIsCaliforniaActive(false);
    return false;
  }

  hasConsentDecision() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy?.currentVisitorConsent) return false;

    const consent = privacy.currentVisitorConsent();
    if (!consent) return false;

    return ['marketing', 'analytics', 'preferences', 'sale_of_data'].some(
      (key) => consent[key] === 'yes' || consent[key] === 'no'
    );
  }

  bindEvents() {
    this.addEventListener('click', this.onComponentClick);

    this.pageOverlay?.addEventListener('click', () => {
      if (this.modalContainer?.classList.contains('california-privacy__modal-container--active')) {
        this.closePreferences();
      }
    });

    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        this.updateSaveButtonState();
      });
    });
  }

  onComponentClick(event) {
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;

    if (button.matches('.js-california-privacy-open-preferences')) {
      event.preventDefault();
      this.showPreferences();
      return;
    }

    if (button.matches('.js-california-privacy-accept-all-banner, .js-california-privacy-accept-all-modal')) {
      event.preventDefault();
      this.applyConsent(this.allConsent(true));
      return;
    }

    if (button.matches('.js-california-privacy-decline-banner, .js-california-privacy-decline-all-modal')) {
      event.preventDefault();
      this.applyConsent(this.allConsent(false));
      return;
    }

    if (button.matches('.js-california-privacy-close')) {
      event.preventDefault();
      this.applyConsent(this.allConsent(false));
      return;
    }

    if (button.matches('.js-california-privacy-save-choices')) {
      event.preventDefault();
      this.applyConsent(this.getCheckboxConsent());
    }
  }

  applyConsent(consent) {
    this.consentSaved = true;
    this.closeAllPopups();

    if (this.isDesignMode()) {
      return;
    }

    try {
      document.dispatchEvent(new CustomEvent('visitorConsentCollected', { detail: consent }));
    } catch (error) {
      // Ignore errors dispatching the event.
    }

    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy?.setTrackingConsent) return;

    try {
      privacy.setTrackingConsent(consent, () => {
        // Reload so consent-gated tags (Klaviyo, Ads, etc.) load under the new
        // consent state instead of staying blocked until the next navigation.
        window.location.reload();
      });
    } catch (error) {
      // Ignore API failures after UI is closed.
    }
  }

  updateOverlayState() {
    const bannerActive = this.banner?.classList.contains('california-privacy__banner--active');
    const modalActive = this.modalContainer?.classList.contains('california-privacy__modal-container--active');

    this.classList.toggle('california-privacy--overlay-visible', Boolean(bannerActive || modalActive));
    this.classList.toggle('california-privacy--modal-open', Boolean(modalActive));
    document.body.classList.toggle(BODY_MODAL_OPEN_CLASS, Boolean(modalActive));
  }

  showBanner() {
    if (this.consentSaved) return;

    this.reveal();
    this.banner?.classList.add('california-privacy__banner--active');
    this.closePreferences(false);
    this.updateOverlayState();
  }

  showPreferences() {
    if (this.consentSaved) return;

    this.reveal();
    this.lastFocusedElement = document.activeElement;
    this.resetCheckboxes();
    this.banner?.classList.add('california-privacy__banner--active');
    this.modalContainer?.removeAttribute('hidden');
    this.modalContainer?.classList.add('california-privacy__modal-container--active');

    if (this.modalBody) {
      this.modalBody.scrollTop = 0;
    }

    this.modal?.focus();
    document.addEventListener('keydown', this.onKeyDown);
    this.updateSaveButtonState();
    this.updateOverlayState();
  }

  closePreferences(restoreFocus = true) {
    this.modalContainer?.classList.remove('california-privacy__modal-container--active');
    this.modalContainer?.setAttribute('hidden', '');
    document.removeEventListener('keydown', this.onKeyDown);

    if (!this.hidden && !this.consentSaved && this.banner && !this.hasConsentDecision()) {
      this.banner.classList.add('california-privacy__banner--active');
    }

    this.updateOverlayState();

    if (restoreFocus && this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }
  }

  closeAllPopups() {
    this.hidden = true;
    this.banner?.classList.remove('california-privacy__banner--active');
    this.modalContainer?.classList.remove('california-privacy__modal-container--active');
    this.modalContainer?.setAttribute('hidden', '');
    this.classList.remove('california-privacy--modal-open');
    document.removeEventListener('keydown', this.onKeyDown);
    this.updateOverlayState();
  }

  allConsent(value) {
    return {
      preferences: value,
      marketing: value,
      analytics: value,
      sale_of_data: value,
    };
  }

  getCheckboxConsent() {
    const values = { preferences: false, marketing: false, analytics: false };

    this.checkboxes.forEach((checkbox) => {
      const key = checkbox.dataset.consentType;
      if (key && Object.prototype.hasOwnProperty.call(values, key)) {
        values[key] = checkbox.checked;
      }
    });

    return {
      preferences: values.preferences,
      marketing: values.marketing,
      analytics: values.analytics,
      sale_of_data: values.marketing,
    };
  }

  resetCheckboxes() {
    this.checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.updateSaveButtonState();
  }

  updateSaveButtonState() {
    if (!this.saveButton) return;

    const hasSelection = Array.from(this.checkboxes).some((checkbox) => checkbox.checked);
    this.saveButton.classList.toggle('california-privacy__btn--active', hasSelection);
  }

  onKeyDown(event) {
    if (!this.modalContainer?.classList.contains('california-privacy__modal-container--active')) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.applyConsent(this.allConsent(false));
      return;
    }

    if (event.key !== 'Tab' || !this.modal) return;

    const focusable = Array.from(this.modal.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null || el === this.modal
    );

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('california-privacy-popup')) {
  customElements.define('california-privacy-popup', CaliforniaPrivacyPopup);
}
